import { inject, Injectable } from '@angular/core';
import { type Observable } from 'rxjs';

import { PageContextFacade } from '@blaze-runtime/engine-data-access';
import { type DialogClosePayload } from '@blaze-runtime/engine-domain';
import { type WorkflowNodeType } from '@blaze-shared/api-contracts-domain';
import { type RuntimeContext } from '@blaze-shared/elements-domain';
import { IntelliSenseService } from '@blaze-shared/intellisense-feature';
import { sentryLog } from '@blaze-shared/logger-util';
import { SchemaService } from '@blaze-shared/schema-data-access';
import { findColumn } from '@blaze-shared/schema-domain';
import {
  type DatasetRow,
  normalizeDataRow,
  type WorkflowSchema,
} from '@blaze-shared/schema-domain';
import {
  type CloseDialogWorkflowNode,
  type ConfirmDialogButtonAction,
  type ConfirmDialogWorkflowNode,
  type DataAssignment,
} from '@blaze-shared/workflow-domain';

import { EngineConfirmDialogs, EngineDialogPages } from '../../../engine-ports/public-api';

export interface DialogOpenRequest {
  assignments: DataAssignment[];
  ctx: RuntimeContext;
  dialogId: string;
  isDrawer?: boolean;
  nodeInput: any;
  nodeType: WorkflowNodeType;
  /** Whether the dialog opens on the node's input rather than empty. */
  passDataAsInput: boolean;
  schema: WorkflowSchema;
}

/**
 * Puts dialogs in front of the user and takes them away again.
 *
 * All three dialog nodes go through the engine's dialog ports: the workflow decides what to ask
 * and with what data, the engine decides how it is rendered.
 */
@Injectable({ providedIn: 'root' })
export class DialogActionService {
  private readonly engineConfirmDialogs = inject(EngineConfirmDialogs);
  private readonly engineDialogPages = inject(EngineDialogPages);
  private readonly iss = inject(IntelliSenseService);
  private readonly pageContextFacade = inject(PageContextFacade);
  private readonly schemaService = inject(SchemaService);

  /**
   * Asks the user to confirm, and reports which button they picked.
   *
   * The message is an expression evaluated against the first input row; values that resolve to
   * nothing are stripped so the user is not shown the words "null" or "undefined".
   */
  confirmDialog(
    node: ConfirmDialogWorkflowNode,
    nodeInput: any,
    ctx: RuntimeContext,
  ): Observable<ConfirmDialogButtonAction> {
    const contextData = Array.isArray(nodeInput) ? nodeInput[0] : nodeInput;
    let context = this.iss.updateRuntimeContext(ctx, contextData);
    context = this.iss.updateRuntimeContextWithStoredVarsDefaults(context, node.nodeType);
    const message = `${this.iss.eval(node.message, context)}`
      .replaceAll('null', '')
      .replaceAll('undefined', '');
    return this.engineConfirmDialogs.openConfirmDialog(
      node.title,
      message,
      node.buttons,
      node.messageFormat,
    );
  }

  /** Dismisses the dialog, passing on the node's output expression when it declares one. */
  dialogClose(node: CloseDialogWorkflowNode, nodeInput: any, ctx: RuntimeContext): void {
    if (!node.outputExpression) {
      this.engineDialogPages.closeDialog(node.dialogId);
      return;
    }
    let context = this.iss.updateRuntimeContext(ctx, nodeInput);
    context = this.iss.updateRuntimeContextWithStoredVarsDefaults(context, node.nodeType);
    this.engineDialogPages.closeDialog(
      node.dialogId,
      this.iss.eval(node.outputExpression, context),
    );
  }

  /**
   * Opens a page as a dialog and waits for it to close.
   *
   * @returns the payload the dialog closes with, which decides whether the workflow carries on.
   */
  dialogOpen({
    assignments,
    ctx,
    dialogId,
    isDrawer,
    nodeInput,
    nodeType,
    passDataAsInput,
    schema,
  }: DialogOpenRequest): Observable<DialogClosePayload> {
    const pageContext = passDataAsInput
      ? this.pageContextFacade.generatePageContext({
          data: this.buildDialogInput({ assignments, ctx, nodeInput, nodeType, schema }),
          formContext: undefined,
          isEmptyishRow: false,
          pageId: dialogId,
          schema,
        })
      : undefined;

    return this.engineDialogPages.openDialog(dialogId, pageContext, isDrawer);
  }

  /** Builds the row the dialog opens on: the node's input, with its assignments applied over it. */
  private buildDialogInput({
    assignments,
    ctx,
    nodeInput,
    nodeType,
    schema,
  }: Pick<
    DialogOpenRequest,
    'assignments' | 'ctx' | 'nodeInput' | 'nodeType' | 'schema'
  >): DatasetRow {
    if (Array.isArray(nodeInput) && nodeInput.length > 1) {
      // Unsupported: reported so we learn whether any app relies on it before dropping it.
      sentryLog('Dialog input is an array of objects. Merging them into one object.', 'warning', {
        jsonPayload: nodeInput,
      });
    }

    const row = normalizeDataRow(nodeInput) ?? {};
    if (!assignments?.length) {
      return row;
    }

    const dialogInput = { ...row };
    let context = this.iss.updateRuntimeContext(ctx, nodeInput);
    context = this.iss.updateRuntimeContextWithStoredVarsDefaults(context, nodeType);
    assignments.forEach(({ name, value }) => {
      const columnName = this.schemaService.getColumnName(name) || '';
      if (!findColumn(schema.columns, columnName)) {
        sentryLog(`Missing column ${columnName} in schema.`, 'warning', {
          schema,
          columnName,
          name,
        });
      }
      dialogInput[columnName] = this.iss.eval(value, context);
    });
    return dialogInput;
  }
}
